import { useState, FormEvent } from 'react';
import { useInView } from '@/hooks/useInView';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const { ref: topRef, inView: topInView } = useInView<HTMLDivElement>({ threshold: 0.15 });
  const { ref: formRef, inView: formInView } = useInView<HTMLDivElement>({ threshold: 0.08 });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://formspree.io/f/xnjorwke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#FAFAF8] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        <div
          ref={topRef}
          className="mb-16 md:mb-20 transition-all duration-700 ease-out"
          style={{ opacity: topInView ? 1 : 0, transform: topInView ? 'none' : 'translateY(20px)' }}
        >
          <p className="text-[#C0706A] text-xs tracking-[0.22em] uppercase font-medium mb-6">
            Get in touch
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="text-[#2C3E50] font-bold leading-tight"
              style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}
            >
              Let&apos;s talk about<br />
              <span className="text-[#C0706A]">the right care.</span>
            </h2>
            <div className="flex flex-col gap-1 text-sm text-[#2C3E50]/50 md:text-right shrink-0">
              <a href="tel:01312100021" className="hover:text-[#C0706A] transition-colors cursor-pointer font-medium text-[#2C3E50]">
                0131 2100021
              </a>
              <a href="mailto:info@alignedhomecare.uk" className="hover:text-[#C0706A] transition-colors cursor-pointer">
                info@alignedhomecare.uk
              </a>
              <span>Scotland, UK</span>
            </div>
          </div>
        </div>

        <div
          ref={formRef}
          className="transition-all duration-700 ease-out"
          style={{ opacity: formInView ? 1 : 0, transform: formInView ? 'none' : 'translateY(24px)', transitionDelay: '100ms' }}
        >
          {submitted ? (
            <div className="py-20 border-t border-[#2C3E50]/10">
              <p className="text-[#2C3E50] font-bold text-3xl mb-3">
                Message received.
              </p>
              <p className="text-[#2C3E50]/50 text-sm">
                A member of our team will be in touch with you shortly.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="aligned-care-contact"
              onSubmit={handleSubmit}
              className="border-t border-[#2C3E50]/10 pt-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-0">
                <div className="border-b md:border-b-0 md:border-r border-[#2C3E50]/10 pb-6 md:pb-0 md:pr-10 mb-6 md:mb-0">
                  <label className="block text-xs text-[#2C3E50]/45 mb-2 tracking-wide">Full name</label>
                  <input
                    name="full_name"
                    type="text"
                    required
                    placeholder="Jane Smith"
                    className="w-full bg-transparent text-[#2C3E50] text-base placeholder-[#2C3E50]/20 focus:outline-none border-b border-[#2C3E50]/15 focus:border-[#C0706A] pb-2 transition-colors text-sm"
                  />
                </div>
                <div className="border-b md:border-b-0 md:border-r border-[#2C3E50]/10 pb-6 md:pb-0 md:px-10 mb-6 md:mb-0">
                  <label className="block text-xs text-[#2C3E50]/45 mb-2 tracking-wide">Email address</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="jane@example.com"
                    className="w-full bg-transparent text-[#2C3E50] text-base placeholder-[#2C3E50]/20 focus:outline-none border-b border-[#2C3E50]/15 focus:border-[#C0706A] pb-2 transition-colors text-sm"
                  />
                </div>
                <div className="pb-6 md:pb-0 md:pl-10 mb-6 md:mb-0">
                  <label className="block text-xs text-[#2C3E50]/45 mb-2 tracking-wide">Phone (optional)</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="07700 900000"
                    className="w-full bg-transparent text-[#2C3E50] text-base placeholder-[#2C3E50]/20 focus:outline-none border-b border-[#2C3E50]/15 focus:border-[#C0706A] pb-2 transition-colors text-sm"
                  />
                </div>
              </div>

              <div className="border-t border-[#2C3E50]/10 pt-8 pb-8 grid grid-cols-1 md:grid-cols-3 gap-0">
                <div className="md:col-span-1 md:pr-10 mb-6 md:mb-0">
                  <label className="block text-xs text-[#2C3E50]/45 mb-2 tracking-wide">I&apos;m enquiring about</label>
                  <select
                    name="enquiry_type"
                    className="w-full bg-transparent text-[#2C3E50] text-sm focus:outline-none border-b border-[#2C3E50]/15 focus:border-[#C0706A] pb-2 transition-colors cursor-pointer appearance-none"
                  >
                    <option value="">Select...</option>
                    <option value="general">General Information</option>
                    <option value="tour">Booking a Tour</option>
                    <option value="availability">Room Availability</option>
                    <option value="memory_care">Memory Care</option>
                    <option value="costs">Costs and Funding</option>
                  </select>
                </div>
                <div className="md:col-span-2 md:pl-10">
                  <label className="block text-xs text-[#2C3E50]/45 mb-2 tracking-wide">
                    Message
                    <span className="ml-2 text-[#2C3E50]/25">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us a bit about your situation..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-transparent text-[#2C3E50] text-sm placeholder-[#2C3E50]/20 focus:outline-none border-b border-[#2C3E50]/15 focus:border-[#C0706A] pb-2 transition-colors resize-none"
                  />
                </div>
              </div>

              <div className="border-t border-[#2C3E50]/10 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[#2C3E50]/30 text-xs leading-relaxed max-w-xs">
                  We&apos;ll respond within one working day. No pressure, no obligation.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#C0706A] hover:bg-[#a85f59] disabled:opacity-50 text-white font-bold px-10 py-3.5 text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 self-start sm:self-auto rounded-lg"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <>Send message <i className="ri-arrow-right-line"></i></>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
